import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import { getProduct, PRODUCTS } from "../data/products";
import { waLink } from "../config/site";
import { WhatsAppIcon } from "../components/DealerSection";
import ProductEnquiryForm from "../components/EnquiryForm";
import { FadeUp, MaskLine } from "../components/Reveal";

const ProductDetail = () => {
  const { slug } = useParams();
  const product = getProduct(slug);

  if (!product) return <Navigate to="/" replace />;

  const related = PRODUCTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <main data-testid="product-detail-page" className="bg-espresso pt-28 md:pt-36">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Link
          data-testid="product-back-link"
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-cream/50 transition-colors hover:text-gold"
        >
          <ArrowLeft size={14} />
          Back to Home
        </Link>

        <div className="mt-10 grid gap-14 lg:grid-cols-2">
          <motion.div
            initial={{ clipPath: "inset(10% 6% 10% 6%)", opacity: 0 }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-sm bg-bark"
          >
            <motion.img
              src={product.image}
              alt={product.name}
              initial={{ scale: 1.15 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
              className="aspect-[4/5] w-full object-cover"
            />
          </motion.div>

          <div>
            <p className="mb-4 text-[11px] uppercase tracking-widest2 text-gold">
              TMV Coffee Collection
            </p>
            <h1 className="font-display text-4xl leading-tight text-cream sm:text-5xl">
              <MaskLine delay={0.1}>{product.name}</MaskLine>
            </h1>
            <FadeUp delay={0.15} className="mt-6 text-base leading-relaxed text-cream/70">
              {product.description}
            </FadeUp>

            <FadeUp delay={0.22} className="mt-8 space-y-4 border-t border-cream/10 pt-8">
              {[
                ["Aroma", product.aroma],
                ["Taste", product.taste],
                ["Suitable for", product.suitable],
              ].map(([label, value]) => (
                <div key={label} className="grid grid-cols-12 gap-3 text-sm">
                  <span className="col-span-4 text-[11px] uppercase tracking-[0.18em] text-sagelight">
                    {label}
                  </span>
                  <span className="col-span-8 text-cream/80">{value}</span>
                </div>
              ))}
            </FadeUp>

            <FadeUp delay={0.28} className="mt-8">
              <p className="mb-4 text-[11px] uppercase tracking-[0.18em] text-sagelight">
                Highlights
              </p>
              <ul className="space-y-3">
                {product.highlights.map((h) => (
                  <li key={h} className="flex items-center gap-3 text-sm text-cream/80">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-sage/30 text-gold">
                      <Check size={11} />
                    </span>
                    {h}
                  </li>
                ))}
              </ul>
            </FadeUp>

            <FadeUp delay={0.34} className="mt-8">
              <p className="mb-4 text-[11px] uppercase tracking-[0.18em] text-sagelight">
                Available pack sizes
              </p>
              <div className="flex flex-wrap gap-3">
                {product.packs.map((size) => (
                  <span
                    key={size}
                    data-testid={`product-pack-size-${size.replace(/\s+/g, "")}`}
                    className="rounded-full border border-cream/20 px-5 py-2 text-xs uppercase tracking-[0.15em] text-cream/75"
                  >
                    {size}
                  </span>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.4} className="mt-10 flex flex-wrap items-center gap-5">
              <span className="font-display text-xl italic text-cream/70">Enquire for Price</span>
              <a
                data-testid="product-whatsapp-enquiry-button"
                href={waLink(`Hello TMV Coffee, I would like to enquire about ${product.name} (${product.packs.join(", ")}). Please share the price.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-espresso transition-all duration-300 hover:bg-cream"
              >
                <WhatsAppIcon className="h-4 w-4" />
                Enquire on WhatsApp
              </a>
            </FadeUp>

            <ProductEnquiryForm product={product} />
          </div>
        </div>

        <div className="py-24 md:py-32">
          <FadeUp>
            <h2 className="mb-10 font-display text-3xl text-cream">More from TMV</h2>
          </FadeUp>
          <div className="grid gap-6 sm:grid-cols-3">
            {related.map((p, i) => (
              <FadeUp key={p.slug} delay={i * 0.08}>
                <Link
                  data-testid={`related-product-link-${p.slug}`}
                  to={`/products/${p.slug}`}
                  className="group block focus:outline-none focus:ring-1 focus:ring-gold"
                >
                  <div className="aspect-[4/5] overflow-hidden rounded-sm bg-bark">
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                    />
                  </div>
                  <h3 className="mt-4 font-display text-lg text-cream transition-colors group-hover:text-gold">
                    {p.name}
                  </h3>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ProductDetail;
